<template>
  <div class="flux-pollo-content" :class="{ 'flux-pollo-content--z-image': i18nKey === 'zImageTurbo' }">
    <section class="flux-block flux-model-explorer">
      <h2>{{ text('exploreTitle') }}</h2>
      <div class="flux-model-links">
        <button v-for="item in exploreModels" :key="item.name" type="button" @click="openModel(item)">
          <span>{{ item.name }}</span>
          <i>&rarr;</i>
        </button>
      </div>
    </section>

    <section class="flux-block flux-key-features">
      <h2>{{ text('mainFeaturesTitle') }}</h2>
      <ul>
        <li v-for="(feature, index) in contentFeatures" :key="feature.name">
          <button type="button" @click="scrollTo('flux-pollo-feature-' + index)">{{ feature.name }}</button>
          <span>{{ feature.short }}</span>
        </li>
      </ul>
    </section>

    <section class="flux-block flux-article-section" :class="{ 'flux-z-prompt-section': i18nKey === 'zImageTurbo' }" id="flux-pollo-feature-0">
      <h2>{{ contentFeatures[0] ? contentFeatures[0].name : text('keyFeaturesTitle') }}</h2>
      <p>{{ contentFeatures[0] ? contentFeatures[0].desc : text('keyFeaturesIntro') }}</p>
      <ul class="flux-benefit-list">
        <li v-for="benefit in qualityBenefits" :key="benefit">{{ benefit }}</li>
      </ul>
      <div class="flux-demo-table">
        <div class="flux-demo-head"><span>{{ $t('seo.imageModels.demoPrompt') }}</span><span>{{ text('generatedImageLabel') }}</span></div>
        <div class="flux-demo-row" v-for="(example, index) in primaryExamples" :key="index">
          <div>
            <p>{{ example.prompt }}</p>
            <button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button>
          </div>
          <figure><img :src="example.image" :alt="example.prompt" loading="lazy"></figure>
        </div>
      </div>
    </section>

    <section class="flux-block flux-article-section" id="flux-pollo-feature-1">
      <h2>{{ text('variantsTitle') }}</h2>
      <p>{{ text('variantsIntro') }}</p>
      <div class="flux-version-table">
        <div class="flux-version-row flux-version-head">
          <span>{{ text('versionLabel') }}</span><span>{{ text('featureLabel') }}</span><span>{{ text('useCaseLabel') }}</span>
        </div>
        <div class="flux-version-row" v-for="variant in contentVariants" :key="variant.name">
          <strong :data-label="text('versionLabel')">{{ variant.name }}</strong><p :data-label="text('featureLabel')">{{ variant.desc }}</p><span :data-label="text('useCaseLabel')">{{ variant.bestFor }}</span>
        </div>
      </div>
    </section>

    <section class="flux-block flux-article-section" id="flux-pollo-feature-3">
      <h2>{{ contentFeatures[3] ? contentFeatures[3].name : text('showcaseTitle') }}</h2>
      <p>{{ contentFeatures[3] ? contentFeatures[3].desc : text('showcaseIntro') }}</p>
      <div class="flux-demo-table flux-demo-table--single">
        <div class="flux-demo-head"><span>{{ $t('seo.imageModels.demoPrompt') }}</span><span>{{ text('generatedImageLabel') }}</span></div>
        <div class="flux-demo-row" v-if="styleExample">
          <div>
            <p>{{ styleExample.prompt }}</p>
            <button type="button" @click="$emit('use-prompt', styleExample.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button>
          </div>
          <figure><img :src="styleExample.image" :alt="styleExample.prompt" loading="lazy"></figure>
        </div>
      </div>
    </section>

    <section class="flux-block flux-article-section" id="flux-pollo-feature-2">
      <h2>{{ contentFeatures[2] ? contentFeatures[2].name : text('keyFeaturesTitle') }}</h2>
      <p>{{ contentFeatures[2] ? contentFeatures[2].desc : text('keyFeaturesIntro') }}</p>
      <figure class="flux-wide-figure">
        <img :src="fallbackImages[3]" :alt="contentFeatures[2] ? contentFeatures[2].name : modelName" loading="lazy">
        <div class="flux-ratio-overlay" aria-hidden="true">
          <span>21:9</span><span>16:9</span><span>4:3</span><span>1:1</span><span>3:4</span><span>9:16</span>
        </div>
      </figure>
    </section>

    <section class="flux-block flux-article-section" id="flux-pollo-feature-4">
      <h2>{{ text('controlsTitle') }}</h2>
      <p>{{ text('controlsIntro') }}</p>
      <div class="flux-option-grid">
        <figure class="flux-option-figure">
          <img :src="fallbackImages[6]" :alt="contentVariants[0] ? contentVariants[0].name : modelName" loading="lazy">
          <figcaption><strong>{{ contentVariants[0] ? contentVariants[0].name : modelName }}</strong><span>{{ contentVariants[0] ? contentVariants[0].desc : text('keyFeaturesIntro') }}</span></figcaption>
        </figure>
        <figure class="flux-option-figure">
          <img :src="fallbackImages[5]" :alt="contentFeatures[3] ? contentFeatures[3].name : modelName" loading="lazy">
          <figcaption><strong>{{ contentFeatures[3] ? contentFeatures[3].name : text('showcaseTitle') }}</strong><span>{{ contentFeatures[3] ? contentFeatures[3].desc : text('showcaseIntro') }}</span></figcaption>
        </figure>
      </div>
    </section>

    <section class="flux-block flux-how-to-section">
      <div class="flux-generator-visual" aria-hidden="true">
        <div class="flux-generator-top"><span>{{ modelName }}</span><strong>7 credits</strong></div>
        <p>{{ text('tutorialPrompt') }}</p>
        <div class="flux-generator-chips"><span>1:1</span><span>16:9</span><span>9:16</span></div>
        <div class="flux-generator-bottom"><span>0/2000</span><button type="button" @click="useTutorialPrompt">{{ text('howToCta') }}</button></div>
      </div>
      <div class="flux-how-to-content">
        <h2>{{ text('howToTitle') }}</h2>
        <p>{{ text('howToIntro') }}</p>
        <article v-for="(step, index) in contentHowToSteps" :key="step.title">
          <strong>0{{ index + 1 }}</strong>
          <div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div>
        </article>
        <button type="button" @click="useTutorialPrompt">{{ text('howToCta') }}</button>
      </div>
    </section>

    <section class="flux-block flux-media-section">
      <h2>{{ text('galleryTitle') }}</h2>
      <div class="flux-media-track">
        <article v-for="(item, index) in mediaExamples" :key="index">
          <figure><img :src="item.image" :alt="item.prompt" loading="lazy"></figure>
          <p>{{ item.prompt }}</p>
        </article>
      </div>
    </section>

    <section class="flux-block flux-discussion-section">
      <h2>{{ text('discussionTitle') }}</h2>
      <div class="flux-discussion-grid">
        <blockquote v-for="item in discussionItems" :key="item.q">
          <span>Miral AI</span><h3>{{ item.q }}</h3><p>{{ item.a }}</p>
        </blockquote>
      </div>
    </section>

    <section class="flux-block flux-community-section">
      <h2>{{ text('communityTitle') }}</h2>
      <div class="flux-community-grid">
        <blockquote v-for="(item, index) in contentUseCases" :key="item.name">
          <span>0{{ index + 1 }}</span><p>{{ item.desc }}</p><footer>{{ item.name }}</footer>
        </blockquote>
      </div>
    </section>

    <section class="flux-block flux-faq-section">
      <h2>{{ text('faqTitle') }}</h2>
      <div class="flux-faq-list">
        <details v-for="faq in contentFaqs" :key="faq.q">
          <summary><span>{{ faq.q }}</span><i></i></summary><p>{{ faq.a }}</p>
        </details>
      </div>
    </section>

    <section class="flux-block flux-bottom-cta">
      <div class="flux-cta-collage">
        <img v-for="(image, index) in ctaImages" :key="index" :src="image" :alt="text('ctaTitle') + ' ' + (index + 1)" loading="lazy">
      </div>
      <div><h2>{{ text('ctaTitle') }}</h2><p>{{ text('ctaDesc') }}</p><button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button></div>
    </section>
  </div>
</template>

<script>
var FALLBACK_IMAGES = {
  flux1: [
    require('@/assets/images/flux1/b9d1e267-bb35-4d5c-a196-fae49460749d.webp'),
    require('@/assets/images/flux1/2ab9dde4-0420-4594-b875-a8465741b767.webp'),
    require('@/assets/images/flux1/b8ffe8cb-1d2f-4bfc-897f-1068861614f9.webp'),
    require('@/assets/images/flux1/a5efd8d2-571d-406d-9a01-f30581d4ea3b.webp'),
    require('@/assets/images/flux1/2b870b40-a436-42b9-980f-538e303cdfce.webp'),
    require('@/assets/images/flux1/faded76c-4da1-49bb-98dc-10b8276f7da8.webp'),
    require('@/assets/images/flux1/0dfa0b4b-7722-40f4-9bc0-7bbf9bb1c000.webp')
  ],
  flux2: [
    require('@/assets/images/flux2/1a2627bd-61e0-406a-9308-19f2883d004e.webp'),
    require('@/assets/images/flux2/sample.webp'),
    require('@/assets/images/flux2/57047990-2e26-4b44-bacf-f10bca5ee70d.webp'),
    require('@/assets/images/flux2/b1d818f0-ff1f-4612-b586-62f75e14466c.webp'),
    require('@/assets/images/flux2/2026b767-edcb-4441-b70e-a6d94ae6ebb9.webp'),
    require('@/assets/images/flux2/c2f47f20-00b4-4cec-b972-e7a69d40be56.webp'),
    require('@/assets/images/flux2/2a4b147b-31c2-4ded-a626-d7e209fb751b.webp')
  ],
  stableDiffusion: [
    require('@/assets/images/stableDiffusion/1783501013638.webp'),
    require('@/assets/images/stableDiffusion/1783501332038.webp'),
    require('@/assets/images/stableDiffusion/1783501396803.webp'),
    require('@/assets/images/stableDiffusion/0217835023294345bd2f0b9e6315ce45b86fb4e22ec7496156d5f_0.webp'),
    require('@/assets/images/stableDiffusion/1783501741652.webp'),
    require('@/assets/images/stableDiffusion/1783501817217.webp'),
    require('@/assets/images/stableDiffusion/021783502374347ebd7c24e3cae4a4b683f52566c3e30e2cb26b5_0.webp')
  ],
  zImageTurbo: [
    require('@/assets/images/zImageTurbo/b1057f7b-3e0e-9687-aa55-56cfed42147a0.webp'),
    require('@/assets/images/zImageTurbo/a8dbab92-cfb4-928b-bd20-4f01835aa0ac0.webp'),
    require('@/assets/images/zImageTurbo/b2964bd5-066f-90b6-8c86-91dad79e44120.webp'),
    require('@/assets/images/zImageTurbo/2d5a0c7e-3ef6-9433-be0d-606be80f99e80.webp'),
    require('@/assets/images/zImageTurbo/f688ab6d-a723-969d-8df1-fbac721e66350.webp'),
    require('@/assets/images/zImageTurbo/715e19f5-fea4-9361-a1e3-0f04d595db660.webp'),
    require('@/assets/images/zImageTurbo/b1057f7b-3e0e-9687-aa55-56cfed42147a0.webp')
  ]
}

module.exports = {
  name: 'Flux1SeoContent',
  props: {
    i18nKey: { type: String, default: 'flux1' },
    modelName: { type: String, default: 'FLUX.1' },
    features: { type: Array, default: function () { return [] } },
    variants: { type: Array, default: function () { return [] } },
    useCases: { type: Array, default: function () { return [] } },
    faqs: { type: Array, default: function () { return [] } },
    howToSteps: { type: Array, default: function () { return [] } }
  },
  data: function () { return { fallbackImages: FALLBACK_IMAGES[this.i18nKey] || FALLBACK_IMAGES.flux1 } },
  computed: {
    contentFeatures: function () { return this.features.length ? this.features : this.translatedArray('features') },
    contentVariants: function () { return this.variants.length ? this.variants : this.translatedArray('variants') },
    contentUseCases: function () { return this.useCases.length ? this.useCases : this.translatedArray('useCases') },
    contentFaqs: function () {
      if (this.$te('seo.imageModels.' + this.i18nKey + '.faqs')) return this.faqs
      return this.translatedArray('faqs').concat(this.faqs)
    },
    contentHowToSteps: function () { return this.howToSteps.length ? this.howToSteps : this.translatedArray('howToSteps') },
    qualityBenefits: function () { return this.translatedArray('qualityBenefits') },
    exploreModels: function () {
      if (this.i18nKey === 'stableDiffusion') {
        return [
          { name: 'Stable Diffusion 3.5', action: 'top' },
          { name: 'Z Image Turbo', slug: 'z-image-turbo-ai-image-generator' },
          { name: 'FLUX.2', slug: 'flux-2-ai-image-generator' },
          { name: 'Midjourney', slug: 'midjourney-ai-image-generator' }
        ]
      }
      if (this.i18nKey === 'zImageTurbo') {
        return [
          { name: 'Z Image Turbo', action: 'top' },
          { name: 'Stable Diffusion 3.5', slug: 'stable-diffusion-ai-image-generator' },
          { name: 'FLUX.2', slug: 'flux-2-ai-image-generator' },
          { name: 'Midjourney', slug: 'midjourney-ai-image-generator' }
        ]
      }
      var variants = this.contentVariants.slice(0, 2).map(function (variant) { return { name: variant.name, action: 'top' } })
      var relatedFlux = this.i18nKey === 'flux2'
        ? { name: 'FLUX.1', slug: 'flux-1-ai-image-generator' }
        : { name: 'FLUX.2', slug: 'flux-2-ai-image-generator' }
      return variants.concat([relatedFlux, { name: 'Midjourney', slug: 'midjourney-ai-image-generator' }])
    },
    primaryExamples: function () {
      var first = this.examples(this.contentFeatures[0], 0)
      var second = this.examples(this.contentFeatures[1], 2)
      return first.concat(second).slice(0, 3)
    },
    styleExample: function () { return this.examples(this.contentFeatures[3], 4)[0] || null },
    mediaExamples: function () {
      var self = this
      return this.contentFeatures.slice(0, 4).map(function (feature, index) { return self.examples(feature, index)[0] })
    },
    discussionItems: function () { return this.contentFaqs.slice(2, 4) },
    ctaImages: function () { return [this.fallbackImages[0], this.fallbackImages[5], this.fallbackImages[6]] }
  },
  methods: {
    // 读取当前 FLUX 页面文案，并沿用项目的英文回退规则。
    text: function (field) { return this.$t('seo.imageModels.' + this.i18nKey + '.' + field) },
    // 读取数组型翻译，未覆盖当前语言时使用英文版本。
    translatedArray: function (field) {
      var key = 'seo.imageModels.' + this.i18nKey + '.' + field
      var value = this.$te(key) ? this.$t(key) : this.$t(key, 'en')
      return Array.isArray(value) ? value : []
    },
    // 将单图和表格型案例统一为参考页使用的提示词图片行。
    examples: function (feature, startIndex) {
      if (!feature) return []
      var rows = feature.demo && feature.demo.rows && feature.demo.rows.length ? feature.demo.rows : [feature]
      var self = this
      return rows.map(function (row, index) {
        return { prompt: row.prompt || feature.short || feature.name, image: row.image || feature.image || self.fallbackImages[(startIndex + index) % self.fallbackImages.length] }
      })
    },
    // 平滑定位到主要特点对应的长内容区。
    scrollTo: function (id) {
      var target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // 打开模型入口；当前页面的版本按钮返回首屏，其他模型进入独立 SEO 页面。
    openModel: function (item) {
      if (item.action === 'top') {
        this.$emit('back-to-generator')
        return
      }
      var lang = this.$route.params.lang
      this.$router.push((lang ? '/' + lang : '') + '/image-models/' + item.slug)
    },
    // 将教程示例提示词写入首屏输入框，并复用父页面的平滑回顶逻辑。
    useTutorialPrompt: function () {
      this.$emit('use-prompt', this.text('tutorialPrompt'))
    }
  }
}
</script>

<style scoped>
.flux-pollo-content { width: 100%; max-width: 1060px; margin: 0 auto; color: #18191d; }
.flux-block { margin-bottom: 104px; scroll-margin-top: 88px; }
.flux-block > h2, .flux-article-section > h2 { margin: 0 0 24px; color: #17181c; font-size: clamp(28px, 3.3vw, 40px); line-height: 1.22; letter-spacing: -.035em; text-align: center; }
.flux-block > p, .flux-article-section > p { max-width: 900px; margin: 0 auto 20px; color: #626873; font-size: 15px; line-height: 1.78; text-align: center; }
.flux-model-links { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.flux-model-links button { display: flex; min-height: 74px; align-items: center; justify-content: space-between; padding: 0 20px; border: 1px solid #e2e4e8; border-radius: 12px; background: #fff; color: #202228; cursor: pointer; font-size: 14px; font-weight: 700; text-align: left; transition: border-color .2s, transform .2s; }
.flux-model-links button:hover { transform: translateY(-2px); border-color: #ff79a8; }
.flux-model-links i { color: #f14685; font-size: 18px; font-style: normal; }
.flux-key-features { max-width: 900px; margin-right: auto; margin-left: auto; }
.flux-key-features ul { margin: 0; padding: 0; border-top: 1px solid #e2e4e7; list-style: none; }
.flux-key-features li { display: grid; grid-template-columns: minmax(210px, .65fr) minmax(0, 1.35fr); gap: 24px; padding: 20px 4px; border-bottom: 1px solid #e2e4e7; }
.flux-key-features button { padding: 0; border: 0; background: transparent; color: #e63e7a; cursor: pointer; font-size: 15px; font-weight: 750; text-align: left; }
.flux-key-features li span { color: #656b75; font-size: 14px; line-height: 1.65; }
.flux-benefit-list { display: grid; max-width: 760px; gap: 12px; margin: 26px auto 34px; padding: 0; color: #505660; font-size: 14px; line-height: 1.7; list-style: none; }
.flux-benefit-list li { position: relative; padding: 13px 18px 13px 42px; border: 1px solid #e4e6e9; border-radius: 10px; background: #fafafb; text-align: left; }
.flux-benefit-list li::before { content: ''; position: absolute; top: 20px; left: 20px; width: 7px; height: 7px; border-radius: 50%; background: #e63e7a; }
.flux-demo-table, .flux-version-table { overflow: hidden; margin-top: 36px; border: 1px solid #dcdee2; border-radius: 12px; background: #fff; }
.flux-demo-head { display: grid; grid-template-columns: 1fr 1fr; background: #f2f3f5; color: #3a3e46; font-size: 13px; font-weight: 750; }
.flux-demo-head span { padding: 16px 20px; }
.flux-demo-head span + span { border-left: 1px solid #dcdee2; }
.flux-demo-row { display: grid; grid-template-columns: 1fr 1fr; min-height: 280px; border-top: 1px solid #dcdee2; }
.flux-demo-row > div { display: flex; padding: 28px; flex-direction: column; align-items: flex-start; justify-content: center; }
.flux-demo-row > div p { margin: 0 0 18px; color: #454a54; font-size: 14px; line-height: 1.75; }
.flux-demo-row button { padding: 0; border: 0; background: transparent; color: #e63e7a; cursor: pointer; font-size: 12px; font-weight: 750; }
.flux-demo-row figure { min-width: 0; margin: 0; overflow: hidden; border-left: 1px solid #dcdee2; background: #eef0f2; }
.flux-demo-row img { display: block; width: 100%; height: 100%; min-height: 280px; object-fit: cover; }
.flux-version-row { display: grid; grid-template-columns: .72fr 1.28fr 1fr; gap: 22px; align-items: center; padding: 22px 20px; border-top: 1px solid #dcdee2; }
.flux-version-head { border-top: 0; background: #f2f3f5; color: #3a3e46; font-size: 13px; font-weight: 750; }
.flux-version-row strong { color: #e63e7a; font-size: 14px; }
.flux-version-row p, .flux-version-row > span { margin: 0; color: #5e646e; font-size: 13px; line-height: 1.65; }
.flux-version-head span { color: #3a3e46; }
.flux-wide-figure, .flux-option-figure { position: relative; max-width: 920px; margin: 34px auto 0; overflow: hidden; border-radius: 14px; background: #eef0f2; }
.flux-wide-figure img, .flux-option-figure img { display: block; width: 100%; max-height: 560px; object-fit: cover; }
.flux-ratio-overlay { position: absolute; right: 24px; bottom: 24px; left: 24px; display: flex; justify-content: center; gap: 8px; padding: 14px; border-radius: 10px; background: rgba(15, 17, 22, .72); backdrop-filter: blur(10px); }
.flux-ratio-overlay span { padding: 7px 10px; border-radius: 6px; background: rgba(255,255,255,.14); color: #fff; font-size: 11px; font-weight: 700; }
.flux-option-grid { display: grid; max-width: 920px; grid-template-columns: repeat(2, 1fr); gap: 18px; margin: 34px auto 0; }
.flux-option-grid .flux-option-figure { margin: 0; }
.flux-option-grid .flux-option-figure img { height: 320px; }
.flux-option-figure figcaption { display: flex; min-height: 122px; box-sizing: border-box; padding: 17px 18px; flex-direction: column; gap: 8px; color: #656b74; background: #f5f6f7; font-size: 13px; line-height: 1.6; }
.flux-option-figure figcaption strong { color: #282c33; font-size: 14px; }
.flux-how-to-section { display: grid; grid-template-columns: .9fr 1.1fr; gap: 62px; align-items: center; }
.flux-generator-visual { display: flex; min-height: 520px; box-sizing: border-box; padding: 34px; border: 1px solid #dfe2e6; border-radius: 16px; background: radial-gradient(circle at 86% 10%, rgba(102, 226, 189, .35), transparent 30%), linear-gradient(145deg, #f8f9fa, #eef1f5); flex-direction: column; box-shadow: 0 20px 45px rgba(30, 34, 42, .08); }
.flux-generator-top, .flux-generator-bottom { display: flex; align-items: center; justify-content: space-between; }
.flux-generator-top span { font-size: 15px; font-weight: 800; }
.flux-generator-top strong { padding: 7px 10px; border-radius: 999px; background: #eaf8f2; color: #087b5d; font-size: 11px; }
.flux-generator-visual > p { flex: 1; margin: 52px 0 24px; color: #4f5661; font-size: 16px; line-height: 1.75; }
.flux-generator-chips { display: flex; gap: 8px; margin-bottom: 26px; }
.flux-generator-chips span { padding: 8px 10px; border: 1px solid #d8dce1; border-radius: 8px; background: #fff; color: #626873; font-size: 11px; }
.flux-generator-bottom { padding-top: 20px; border-top: 1px solid #dfe2e6; }
.flux-generator-bottom span { color: #9aa0a9; font-size: 11px; }
.flux-generator-bottom button { padding: 11px 16px; border: 0; border-radius: 9px; background: #17191e; color: #fff; cursor: pointer; font-size: 11px; font-weight: 750; }
.flux-how-to-content > h2 { margin: 0 0 14px; font-size: clamp(28px, 3.3vw, 40px); line-height: 1.2; letter-spacing: -.035em; }
.flux-how-to-content > p { margin: 0 0 28px; color: #666c76; font-size: 14px; line-height: 1.7; }
.flux-how-to-content article { display: grid; grid-template-columns: 48px 1fr; gap: 18px; padding: 20px 0; border-top: 1px solid #e2e4e7; }
.flux-how-to-content article > strong { color: #e63e7a; font-size: 22px; }
.flux-how-to-content h3 { margin: 0 0 7px; font-size: 15px; }
.flux-how-to-content article p { margin: 0; color: #686e77; font-size: 13px; line-height: 1.65; }
.flux-how-to-content > button, .flux-bottom-cta button { margin-top: 22px; padding: 13px 24px; border: 0; border-radius: 9px; background: #17191e; color: #fff; cursor: pointer; font-size: 13px; font-weight: 750; }
.flux-media-track { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.flux-media-track article { overflow: hidden; border: 1px solid #e2e4e7; border-radius: 12px; background: #fff; }
.flux-media-track figure { position: relative; aspect-ratio: 16 / 9; margin: 0; overflow: hidden; background: #eceef0; }
.flux-media-track img { width: 100%; height: 100%; object-fit: cover; }
.flux-media-track p { display: -webkit-box; overflow: hidden; margin: 0; padding: 14px; color: #444a53; font-size: 12px; line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.flux-discussion-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.flux-discussion-grid blockquote, .flux-community-grid blockquote { margin: 0; padding: 26px; border: 1px solid #e2e4e7; border-radius: 14px; background: #fff; }
.flux-discussion-grid span { color: #e63e7a; font-size: 11px; font-weight: 800; }
.flux-discussion-grid h3 { margin: 20px 0 10px; font-size: 18px; }
.flux-discussion-grid p, .flux-community-grid p { margin: 0; color: #626873; font-size: 13px; line-height: 1.7; }
.flux-community-grid { columns: 3; column-gap: 16px; }
.flux-community-grid blockquote { margin-bottom: 16px; break-inside: avoid; }
.flux-community-grid blockquote > span { color: #e63e7a; font-size: 11px; font-weight: 800; }
.flux-pollo-content--z-image .flux-model-links button:hover { border-color: #7770f5; }
.flux-pollo-content--z-image .flux-model-links i,
.flux-pollo-content--z-image .flux-key-features button,
.flux-pollo-content--z-image .flux-demo-row button,
.flux-pollo-content--z-image .flux-version-row strong,
.flux-pollo-content--z-image .flux-how-to-content article > strong,
.flux-pollo-content--z-image .flux-discussion-grid span,
.flux-pollo-content--z-image .flux-community-grid blockquote > span { color: #655de9; }
.flux-pollo-content--z-image .flux-benefit-list li::before { background: #655de9; }
.flux-pollo-content--z-image .flux-generator-visual { background: radial-gradient(circle at 86% 10%, rgba(133,124,255,.25), transparent 31%), linear-gradient(145deg, #faf9ff, #eef1f7); }
.flux-z-prompt-section { padding: 52px 48px; border-radius: 24px; background: radial-gradient(circle at 88% 8%, rgba(130,121,255,.18), transparent 28%), linear-gradient(145deg, #faf9ff, #f1f3fa); }
.flux-z-prompt-section .flux-benefit-list { max-width: none; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.flux-z-prompt-section .flux-benefit-list li { padding: 18px 16px 18px 38px; background: rgba(255,255,255,.82); }
.flux-z-prompt-section .flux-benefit-list li::before { top: 25px; left: 18px; }
.flux-z-prompt-section .flux-demo-table { overflow: visible; border: 0; background: transparent; }
.flux-z-prompt-section .flux-demo-head { display: none; }
.flux-z-prompt-section .flux-demo-row { overflow: hidden; grid-template-columns: .82fr 1.18fr; min-height: 260px; margin-bottom: 14px; border: 1px solid #dfe1ea; border-radius: 15px; background: #fff; box-shadow: 0 10px 28px rgba(35,39,54,.055); }
.flux-z-prompt-section .flux-demo-row:last-child { margin-bottom: 0; }
.flux-z-prompt-section .flux-demo-row > div { padding: 30px; }
.flux-z-prompt-section .flux-demo-row img { min-height: 260px; }
.flux-community-grid footer { margin-top: 24px; color: #30343b; font-size: 12px; font-weight: 750; }
.flux-faq-section { max-width: 860px; margin-right: auto; margin-left: auto; }
.flux-faq-list { border-top: 1px solid #dfe1e5; }
.flux-faq-list details { border-bottom: 1px solid #dfe1e5; }
.flux-faq-list summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 2px; cursor: pointer; font-size: 15px; font-weight: 700; list-style: none; }
.flux-faq-list summary::-webkit-details-marker { display: none; }
.flux-faq-list summary i { position: relative; width: 15px; height: 15px; flex: 0 0 15px; }
.flux-faq-list summary i::before, .flux-faq-list summary i::after { content: ''; position: absolute; top: 7px; left: 1px; width: 13px; height: 1px; background: #6d737c; }
.flux-faq-list summary i::after { transform: rotate(90deg); transition: transform .2s; }
.flux-faq-list details[open] summary i::after { transform: rotate(0); }
.flux-faq-list details p { max-width: 760px; margin: -2px 0 22px; color: #656b75; font-size: 14px; line-height: 1.78; }
.flux-bottom-cta { position: relative; display: grid; grid-template-columns: .82fr 1.18fr; min-height: 360px; overflow: hidden; border-radius: 18px; background: #15171c; color: #fff; }
.flux-cta-collage { display: grid; height: 100%; grid-template-columns: repeat(2, 1fr); grid-template-rows: repeat(2, 1fr); gap: 2px; background: #15171c; }
.flux-cta-collage img { width: 100%; height: 100%; min-height: 0; object-fit: cover; }
.flux-cta-collage img:first-child { grid-row: 1 / 3; }
.flux-bottom-cta > div { align-self: center; padding: 48px; }
.flux-bottom-cta h2 { margin: 0 0 14px; color: #fff; font-size: clamp(28px, 3.2vw, 40px); line-height: 1.18; text-align: left; }
.flux-bottom-cta p { margin: 0; color: #bfc3ca; font-size: 14px; line-height: 1.72; }
.flux-bottom-cta button { background: #fff; color: #17191e; }
@media (max-width: 900px) {
  .flux-model-links, .flux-media-track { grid-template-columns: repeat(2, 1fr); }
  .flux-how-to-section { grid-template-columns: 1fr; gap: 36px; }
  .flux-generator-visual { min-height: 440px; }
  .flux-community-grid { columns: 2; }
  .flux-z-prompt-section .flux-benefit-list { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .flux-block { margin-bottom: 76px; }
  .flux-block > h2, .flux-article-section > h2 { font-size: 28px; }
  .flux-model-links, .flux-media-track, .flux-discussion-grid { grid-template-columns: 1fr; }
  .flux-key-features li, .flux-demo-head, .flux-demo-row, .flux-version-row { grid-template-columns: 1fr; }
  .flux-demo-head span + span { display: none; }
  .flux-demo-row > div { padding: 22px; }
  .flux-demo-row figure { border-top: 1px solid #dcdee2; border-left: 0; }
  .flux-version-head { display: none; }
  .flux-version-row { gap: 8px; }
  .flux-version-row > *::before { content: attr(data-label); display: block; margin-bottom: 4px; color: #969ca5; font-size: 10px; font-weight: 700; text-transform: uppercase; }
  .flux-ratio-overlay { right: 10px; bottom: 10px; left: 10px; overflow-x: auto; justify-content: flex-start; }
  .flux-option-grid { grid-template-columns: 1fr; }
  .flux-generator-visual { min-height: 360px; padding: 24px; }
  .flux-community-grid { columns: 1; }
  .flux-z-prompt-section { margin-right: -18px; margin-left: -18px; padding: 42px 18px; border-radius: 0; }
  .flux-z-prompt-section .flux-demo-row { grid-template-columns: 1fr; }
  .flux-bottom-cta { grid-template-columns: 1fr; margin-right: -18px; margin-left: -18px; border-radius: 0; }
  .flux-cta-collage { height: 280px; }
  .flux-bottom-cta > div { padding: 38px 22px; }
}
</style>
